import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Dumbbell, GripVertical, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RecoveryMinSecInputs from './RecoveryMinSecInputs';
import WorkoutRepsInputs from './WorkoutRepsInputs';
import PyramidSeriesEditor from './PyramidSeriesEditor';
import { GYM_WORKOUT_SET_TYPE_OPTIONS, getWorkoutSetTypeLabel, getWorkoutSetTypeBadgeClass } from '../../../constants/workoutSetTypes';
import { formatWorkoutRepsTarget } from '../../../constants/workoutRepsMode';
import { isPyramidExercise, pyramidDisplayReps } from '../../../constants/workoutSeriesPlan';

const inputClass =
    'w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500';

function exerciseSummaryLine(exercise) {
    const reps = formatWorkoutRepsTarget(exercise) || exercise.ripetizioni || 0;
    const parts = [`${exercise.serie || 0} × ${reps}`];
    if (exercise.is_cardio !== 1 && Number(exercise.peso) > 0) {
        parts.push(`@ ${exercise.peso} kg`);
    }
    return parts.join(' ');
}

function formatMuscleLabel(muscle) {
    if (!muscle) return null;
    if (typeof muscle === 'string') return muscle;
    if (typeof muscle === 'object' && muscle.name) return muscle.name;
    return null;
}

function getExerciseMuscleLabel(exercise) {
    return formatMuscleLabel(exercise.primary_muscle) || exercise.gruppo_muscolare || null;
}

export default function WorkoutSortableExerciseItem({
    exercise,
    previousExercise,
    onUpdate,
    onRemove,
    onToggleDetails,
    isExpanded,
    isFirst,
}) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: exercise.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    const muscleLabel = getExerciseMuscleLabel(exercise);

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`bg-white dark:bg-gray-900 ${isDragging ? 'z-10 shadow-md' : ''}`}
        >
            <div className="flex items-center gap-1.5 px-2 py-2 sm:gap-2 sm:px-3">
                <button
                    type="button"
                    {...attributes}
                    {...listeners}
                    className="shrink-0 cursor-grab rounded p-1 text-gray-400 hover:text-indigo-600 active:cursor-grabbing dark:hover:text-indigo-300"
                    aria-label={t('gym.membersManager.workoutModal.dragExercise')}
                >
                    <GripVertical className="h-4 w-4" aria-hidden />
                </button>

                <div className="h-8 w-8 shrink-0 overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800">
                    {exercise.immagine_nome ? (
                        <img
                            src={`/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_nome)}`}
                            alt=""
                            className="h-full w-full object-cover select-none"
                            onContextMenu={(e) => e.preventDefault()}
                            draggable={false}
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    ) : (
                        <span className="flex h-full w-full items-center justify-center">
                            <Dumbbell className="h-3.5 w-3.5 text-gray-400 dark:text-white/40" aria-hidden />
                        </span>
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-white" title={exercise.nome}>
                        {exercise.nome}
                    </p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-white/55">
                        <span className="font-medium text-gray-700 dark:text-white/75">{exerciseSummaryLine(exercise)}</span>
                        {muscleLabel ? (
                            <span className="truncate">{muscleLabel}</span>
                        ) : null}
                    </p>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                        {exercise.set_type && exercise.set_type !== 'normal' ? (
                            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${getWorkoutSetTypeBadgeClass(exercise.set_type)}`}>
                                {getWorkoutSetTypeLabel(exercise.set_type)}
                            </span>
                        ) : null}
                        {exercise.linked_to_previous ? (
                            <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">
                                {t('gym.membersManager.workouts.detail.linkedBadge')}
                            </span>
                        ) : null}
                        {isPyramidExercise(exercise) && pyramidDisplayReps(exercise) ? (
                            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                                {pyramidDisplayReps(exercise)}
                            </span>
                        ) : null}
                        {exercise.is_cardio === 1 ? (
                            <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-800 dark:bg-red-900/40 dark:text-red-200">
                                Cardio
                            </span>
                        ) : null}
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-0.5">
                    <button
                        type="button"
                        onClick={() => onToggleDetails(exercise.id)}
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-white/70 dark:hover:bg-white/10"
                        title={isExpanded ? t('gym.membersManager.workoutModal.hideParameters') : t('gym.membersManager.workoutModal.configureParameters')}
                        aria-expanded={isExpanded}
                    >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    <button
                        type="button"
                        onClick={() => onRemove(exercise.id)}
                        className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                        title={t('gym.membersManager.workoutModal.removeExercise')}
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {isExpanded ? (
                <div className="border-t border-gray-100 bg-gray-50/80 px-3 py-3 dark:border-white/5 dark:bg-gray-800/40 sm:px-4">
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        {!isPyramidExercise(exercise) ? (
                            <div>
                                <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">Serie</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={exercise.serie ?? ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        onUpdate(exercise.id, 'serie', value === '' ? '' : parseInt(value, 10) || '');
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value === '' || e.target.value === '0') {
                                            onUpdate(exercise.id, 'serie', 3);
                                        }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    className={inputClass}
                                />
                            </div>
                        ) : null}

                        {!isPyramidExercise(exercise) ? <WorkoutRepsInputs exercise={exercise} onUpdate={onUpdate} /> : null}

                        {exercise.is_cardio !== 1 && !isPyramidExercise(exercise) ? (
                            <div>
                                <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">Peso (kg)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={exercise.peso ?? ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        onUpdate(exercise.id, 'peso', value === '' ? '' : parseFloat(value) || '');
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value === '') {
                                            onUpdate(exercise.id, 'peso', 0);
                                        }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    className={inputClass}
                                />
                            </div>
                        ) : null}

                        <RecoveryMinSecInputs
                            valueSeconds={exercise.tempo_recupero}
                            onSecondsChange={(sec) => onUpdate(exercise.id, 'tempo_recupero', sec)}
                            defaultTotalSeconds={90}
                            focusColor="emerald"
                            gymDarkInputs
                        />
                    </div>

                    <div className="mt-3">
                        <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">Tipo di Serie</label>
                        <select
                            value={exercise.set_type || 'normal'}
                            onChange={(e) => onUpdate(exercise.id, 'set_type', e.target.value)}
                            className={inputClass}
                        >
                            {GYM_WORKOUT_SET_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <PyramidSeriesEditor exercise={exercise} onUpdate={onUpdate} />

                    {!isFirst && exercise.set_type && exercise.set_type !== 'normal' && previousExercise && exercise.set_type === previousExercise.set_type ? (
                        <div className="mt-3">
                            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-white/85">
                                <input
                                    type="checkbox"
                                    checked={exercise.linked_to_previous || false}
                                    onChange={(e) => onUpdate(exercise.id, 'linked_to_previous', e.target.checked)}
                                    className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                Collegato all&apos;esercizio precedente
                            </label>
                        </div>
                    ) : null}

                    {exercise.is_cardio !== 1 ? (
                        <div className="mt-3">
                            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-white/85">
                                <input
                                    type="checkbox"
                                    checked={exercise.is_isometric_scheda === 1}
                                    onChange={(e) => onUpdate(exercise.id, 'is_isometric_scheda', e.target.checked ? 1 : 0)}
                                    className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                Modalità isometrica (secondi)
                            </label>
                        </div>
                    ) : null}

                    {exercise.is_cardio === 1 ? (
                        <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                                <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">Calorie (opz.)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={exercise.calorie || ''}
                                    onChange={(e) => onUpdate(exercise.id, 'calorie', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    className={inputClass}
                                    placeholder="Calorie"
                                />
                            </div>
                            <div>
                                <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">BPM (opz.)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={exercise.bpm || ''}
                                    onChange={(e) => onUpdate(exercise.id, 'bpm', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    className={inputClass}
                                    placeholder="BPM"
                                />
                            </div>
                        </div>
                    ) : null}

                    <div className="mt-3">
                        <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">Note trainer</label>
                        <textarea
                            value={exercise.notes?.trainer ?? ''}
                            onChange={(e) => {
                                const existingNotes = exercise.notes || { trainer: null, user: null, system: null };
                                onUpdate(exercise.id, 'notes', {
                                    trainer: e.target.value || null,
                                    user: existingNotes.user || null,
                                    system: existingNotes.system || null,
                                });
                            }}
                            rows={2}
                            maxLength={500}
                            className={inputClass}
                            placeholder="Istruzioni per il cliente durante l'allenamento"
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
}
